import React, {
  createContext,
  useState,
  useEffect,
  ReactNode,
  useContext,
} from "react";
import {
  ApiDataResponse,
  AssessmentType,
  BehavioralSubject,
  Class,
  GradeCriteria,
  Level,
  PromotionRule,
  School,
  SchoolWithDependencies,
  Session,
  Subject,
  Term,
  User,
} from "../services/Dtos";
import { useUserContext } from "./userContext";
import useAxios from "../hooks/useAxios";
import { useGlobalError } from "./GlobalErrorContext";

interface SchoolsContextType {
  schools: School[];
  setSchools: React.Dispatch<React.SetStateAction<School[]>>;
  school: School | null;
  setSchool: React.Dispatch<React.SetStateAction<School | null>>;
  sessions: Session[];
  setSessions: React.Dispatch<React.SetStateAction<Session[]>>;
  terms: Term[];
  setTerms: React.Dispatch<React.SetStateAction<Term[]>>;
  gradeCriterias: GradeCriteria[];
  setGradeCriterias: React.Dispatch<React.SetStateAction<GradeCriteria[]>>;
  assessmentTypes: AssessmentType[];
  setAssessmentTypes: React.Dispatch<React.SetStateAction<AssessmentType[]>>;
  levels: Level[];
  setLevels: React.Dispatch<React.SetStateAction<Level[]>>;
  promotionRules: PromotionRule[];
  setPromotionRules: React.Dispatch<React.SetStateAction<PromotionRule[]>>;
  classes: Class[];
  setClasses: React.Dispatch<React.SetStateAction<Class[]>>;
  subjects: Subject[];
  setSubjects: React.Dispatch<React.SetStateAction<Subject[]>>;
  behavioralSubjects: BehavioralSubject[];
  setBehavioralSubjects: React.Dispatch<
    React.SetStateAction<BehavioralSubject[]>
  >;
  users: User[];
  setUsers: React.Dispatch<React.SetStateAction<User[]>>;
  error: string | null;
  isLoading: boolean;
  setIsLoading: React.Dispatch<React.SetStateAction<boolean>>;
  accentColor: string;
}

interface Props {
  children: ReactNode;
}

const SchoolsContext = createContext<SchoolsContextType | undefined>(undefined);

export const SchoolsProvider = ({ children }: Props) => {
  const [schools, setSchools] = useState<School[]>([]);
  const [school, setSchool] = useState<School | null>(null);
  const [sessions, setSessions] = useState<Session[]>([]);
  const [terms, setTerms] = useState<Term[]>([]);
  const [gradeCriterias, setGradeCriterias] = useState<GradeCriteria[]>([]);
  const [assessmentTypes, setAssessmentTypes] = useState<AssessmentType[]>([]);
  const [levels, setLevels] = useState<Level[]>([]);
  const [promotionRules, setPromotionRules] = useState<PromotionRule[]>([]);
  const [classes, setClasses] = useState<Class[]>([]);
  const [subjects, setSubjects] = useState<Subject[]>([]);
  const [behavioralSubjects, setBehavioralSubjects] = useState<
    BehavioralSubject[]
  >([]);
  const [users, setUsers] = useState<User[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { user, isLoading: userLoading } = useUserContext();
  const axiosClient = useAxios();
  const { setError: setGlobalError } = useGlobalError();
  const [accentColor, setAccentColor] = useState<string>("brand");

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!user || !token || userLoading) {
      setIsLoading(false);
      return;
    }
    const fetchData = async () => {
      setIsLoading(true);
      try {
        setError(null);
        // Fetch schools
        const schoolsResponse = await axiosClient.get<
          ApiDataResponse<SchoolWithDependencies>
        >(`schools/GetSchoolWithDependencies/${user.schoolId}`);
        if (schoolsResponse.data?.succeeded) {
          setSchool(schoolsResponse.data.data.schoolInfo);
          setSessions(
            schoolsResponse.data.data.sessions.sort(
              (a: any, b: any) => a.order - b.order
            ).reverse()
          );
          setTerms(
            schoolsResponse.data.data.terms.sort(
              (a: any, b: any) => a.order - b.order
            )
          );
          setLevels(
            schoolsResponse.data.data.levels.sort(
              (a: any, b: any) => a.order - b.order
            )
          );

          setPromotionRules(schoolsResponse.data.data.promotionRules);
          setClasses(
            schoolsResponse.data.data.classes.sort(
              (a: any, b: any) => a.order - b.order
            )
          );
          setSubjects(
            schoolsResponse.data.data.subjects.sort((a: any, b: any) =>
              a.subjectName.localeCompare(b.subjectName)
            )
          );
          setBehavioralSubjects(
            schoolsResponse.data.data.behavioralSubjects.sort(
              (a: any, b: any) => a.name.localeCompare(b.name)
            )
          );
          setGradeCriterias(
            schoolsResponse.data.data.gradeCriterias.sort(
              (a: any, b: any) => a.order - b.order
            )
          );
          setAssessmentTypes(
            schoolsResponse.data.data.assessmentTypes.sort(
              (a: any, b: any) => a.order - b.order
            )
          );
        } else {
          const errorMessage = `Error fetching schools: ${schoolsResponse.data?.message}`;
          setError(errorMessage);
          setGlobalError({
            message: errorMessage,
            onRetry: () => window.location.reload(),
          });
          return;
        }

        // Fetch users
        const usersResponse =
          await axiosClient.get<ApiDataResponse<User[]>>("users");
        if (usersResponse.data?.succeeded) {
      
          setUsers(usersResponse.data.data);
        } else {
          const errorMessage = `Error fetching users: ${usersResponse.data?.message}`;
          setError(errorMessage);
          setGlobalError({
            message: errorMessage,
            onRetry: () => window.location.reload(),
          });
          return;
        }
      } catch (error) {
        const errorMessage = "Failed to fetch data";
        setError(errorMessage);
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, [user, userLoading]);

  useEffect(() => {
    if (school) {
      setAccentColor(school.accentColor || "brand");
    }
  }, [school]);

  return (
    <SchoolsContext.Provider
      value={{
        schools,
        setSchools,
        school,
        setSchool,
        sessions,
        setSessions,
        terms,
        setTerms,
        gradeCriterias,
        setGradeCriterias,
        assessmentTypes,
        setAssessmentTypes,
        levels,
        promotionRules,
        setPromotionRules,
        classes,
        setClasses,
        subjects,
        setSubjects,
        setLevels,
        users,
        setUsers,
        error,
        isLoading,
        setIsLoading,
        behavioralSubjects,
        setBehavioralSubjects,
        accentColor,
      }}
    >
      {children}
    </SchoolsContext.Provider>
  );
};

export const useSchoolsContext = () => {
  const context = useContext(SchoolsContext);
  if (!context) {
    throw new Error("useSchoolsContext must be used within a SchoolsProvider");
  }
  return context;
};
